import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Languages } from 'lucide-react';
import { LOGOS } from '@/i18n';

const Navbar = ({ lang, setLang, t }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { key: 'services', href: '#services', label: t.nav.services },
    { key: 'credentials', href: '#credentials', label: t.nav.credentials },
    { key: 'about', href: '#about', label: t.nav.about },
    { key: 'tech', href: '#tech', label: t.nav.tech },
    { key: 'blog', href: '#blog', label: t.nav.blog },
    { key: 'contact', href: '#contact', label: t.nav.contact },
  ];

  const handleNav = (e, href) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === '/') {
      const target = document.querySelector(href);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Navigate to landing with hash; LandingPage useEffect handles scroll
      navigate('/' + href);
    }
  };

  return (
    <header
      className={`sticky top-0 z-50 border-b transition-[background,border] duration-200 ${
        scrolled ? 'border-white/10 bg-[rgb(var(--bg-0))]' : 'border-transparent bg-[rgb(var(--bg-0))]'
      }`}
      data-testid="navbar"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo" aria-label="Cauratech">
            <img src={LOGOS.logo_horizontal_dark} alt="Cauratech" className="h-9 sm:h-10 w-auto" />
          </Link>

          <nav className="hidden xl:flex items-center gap-6" data-testid="navbar-links">
            {navItems.map((it) => (
              <a
                key={it.key}
                href={it.href}
                onClick={(e) => handleNav(e, it.href)}
                className="text-sm text-white/80 hover:text-white transition-colors duration-200"
                data-testid={`nav-link-${it.key}`}
              >
                {it.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="language-toggle">
              <button
                onClick={() => setLang('es')}
                className={`px-3 py-1 text-xs font-mono-accent rounded-full transition-colors ${
                  lang === 'es' ? 'bg-[rgb(var(--brand-electric))] text-white' : 'text-white/70 hover:text-white'
                }`}
                data-testid="lang-es"
                aria-pressed={lang === 'es'}
              >ES</button>
              <button
                onClick={() => setLang('en')}
                className={`px-3 py-1 text-xs font-mono-accent rounded-full transition-colors ${
                  lang === 'en' ? 'bg-[rgb(var(--brand-electric))] text-white' : 'text-white/70 hover:text-white'
                }`}
                data-testid="lang-en"
                aria-pressed={lang === 'en'}
              >EN</button>
            </div>

            <Link to="/agenda" className="btn-primary hidden md:inline-flex" data-testid="navbar-primary-cta">
              {t.meta.cta_primary}
            </Link>

            <button
              className="xl:hidden p-2 text-white"
              aria-label="menu"
              onClick={() => setOpen(v => !v)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden pb-4" data-testid="mobile-menu">
            <div className="flex flex-col gap-1 border-t border-white/10 pt-3">
              {navItems.map((it) => (
                <a
                  key={it.key}
                  href={it.href}
                  onClick={(e) => handleNav(e, it.href)}
                  className="px-3 py-3 rounded-lg text-sm text-white/85 hover:bg-white/5"
                  data-testid={`nav-link-mobile-${it.key}`}
                >
                  {it.label}
                </a>
              ))}
              <div className="flex items-center gap-2 mt-2 px-2 sm:hidden">
                <Languages size={16} className="text-white/60" />
                <div className="flex items-center rounded-full border border-white/10 bg-white/[0.03] p-1">
                  <button onClick={() => setLang('es')} className={`px-3 py-1 text-xs font-mono-accent rounded-full ${lang === 'es' ? 'bg-[rgb(var(--brand-electric))] text-white' : 'text-white/70'}`}>ES</button>
                  <button onClick={() => setLang('en')} className={`px-3 py-1 text-xs font-mono-accent rounded-full ${lang === 'en' ? 'bg-[rgb(var(--brand-electric))] text-white' : 'text-white/70'}`}>EN</button>
                </div>
              </div>
              <Link
                to="/agenda"
                onClick={() => setOpen(false)}
                className="btn-primary mt-3 w-full"
                data-testid="navbar-primary-cta-mobile"
              >
                {t.meta.cta_primary}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
